import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {SavedSearch, SavedSearchCreate, SavedSearchUpdate} from "../schemas";
import {useGroupContext} from "./group_context";
import {getSavedSearches, createSavedSearch as createSavedSearchApi, updateSavedSearch as updateSavedSearchApi, deleteSavedSearch as deleteSavedSearchApi} from "../api";
import {withApiErrorHandlerThrow} from "../error_handling";


export interface SavedSearchContextType {
    savedSearches: SavedSearch[];
    savedSearchLoading: boolean;
    refreshSavedSearches: () => Promise<void>;
    createSavedSearch: (data: Omit<SavedSearchCreate, 'group_id'>) => Promise<SavedSearch>;
    updateSavedSearch: (data: SavedSearchUpdate) => Promise<SavedSearch>;
    deleteSavedSearch: (id: string) => Promise<void>;
}

export const SavedSearchContext = createContext<SavedSearchContextType | undefined>(undefined);

interface SavedSearchProviderProps {
    children: ReactNode;
}

export const SavedSearchProvider: React.FC<SavedSearchProviderProps> = ({children}) => {
    const {currentGroup} = useGroupContext()!;

    const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
    const [savedSearchLoading, setSavedSearchLoading] = useState<boolean>(false);

    const refreshSavedSearches = useCallback(async (): Promise<void> => {
        if (!currentGroup?.id) return;

        setSavedSearchLoading(true);
        try {
            const searches = await withApiErrorHandlerThrow(
                () => getSavedSearches(currentGroup.id),
                {titleQualifier: "Error Fetching Saved Searches"}
            );
            // Ensure we always set an array (API may return null/undefined)
            setSavedSearches(Array.isArray(searches) ? searches : []);
        } finally {
            setSavedSearchLoading(false);
        }
    }, [currentGroup?.id]);

    useEffect(() => {
        if (currentGroup?.id) {
            refreshSavedSearches();
        } else {
            setSavedSearches([]);
        }
    }, [currentGroup?.id, refreshSavedSearches]);

    const createSavedSearch = useCallback(async (data: Omit<SavedSearchCreate, 'group_id'>): Promise<SavedSearch> => {
        if (!currentGroup?.id) {
            throw new Error("No group selected");
        }

        const created = await withApiErrorHandlerThrow(
            () => createSavedSearchApi({
                ...data,
                group_id: currentGroup.id,
            }),
            {titleQualifier: "Error Creating Saved Search"}
        );

        // Optimistic update (defensive check for prev being an array)
        setSavedSearches(prev => [...(Array.isArray(prev) ? prev : []), created].sort((a, b) => a.name.localeCompare(b.name)));

        return created;
    }, [currentGroup?.id]);

    const updateSavedSearch = useCallback(async (data: SavedSearchUpdate): Promise<SavedSearch> => {
        const updated = await withApiErrorHandlerThrow(
            () => updateSavedSearchApi(data),
            {titleQualifier: "Error Updating Saved Search"}
        );

        // Optimistic update (defensive check for prev being an array)
        setSavedSearches(prev =>
            (Array.isArray(prev) ? prev : []).map(ss => ss.id === updated.id ? updated : ss)
                .sort((a, b) => a.name.localeCompare(b.name))
        );

        return updated;
    }, []);

    const deleteSavedSearch = useCallback(async (id: string): Promise<void> => {
        await withApiErrorHandlerThrow(
            () => deleteSavedSearchApi(id),
            {titleQualifier: "Error Deleting Saved Search"}
        );

        // Optimistic update (defensive check for prev being an array)
        setSavedSearches(prev => (Array.isArray(prev) ? prev : []).filter(ss => ss.id !== id));
    }, []);

    const contextValue: SavedSearchContextType = {
        savedSearches,
        savedSearchLoading,
        refreshSavedSearches,
        createSavedSearch,
        updateSavedSearch,
        deleteSavedSearch,
    };

    return (
        <SavedSearchContext.Provider value={contextValue}>
            {children}
        </SavedSearchContext.Provider>
    );
};

export const useSavedSearchContext = () => useContext(SavedSearchContext);